import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, Menu, X } from "lucide-react";
import { CTAButton } from "./primitives";
import { SEGMENT_LIST } from "../../lib/content";

const whyAnchors = [
  { label: "The Opportunity", to: "/why-partner#the-opportunity" },
  { label: "Why Partner With Us", to: "/why-partner#why-us" },
  { label: "The Economics", to: "/why-partner#the-economics" },
];

function Logo() {
  return (
    <Link to="/" className="flex items-center" data-testid="nav-logo">
      <img
        src="/logo-communitytax.webp"
        alt="Community Tax"
        className="h-7 w-auto"
      />
      <span className="sr-only">Community Tax Partners</span>
    </Link>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenMenu(null);
  }, [location]);

  const linkCls =
    "text-sm font-medium text-[#1E1E24] hover:text-[#0262B4] transition-colors duration-200";

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/85 backdrop-blur-xl border-b border-[#D3D6E0]/70" : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-[72px]">
          <Logo />

          <nav className="hidden lg:flex items-center gap-8">
            <div
              className="relative"
              onMouseEnter={() => setOpenMenu("why")}
              onMouseLeave={() => setOpenMenu(null)}
            >
              <Link to="/why-partner" className={`${linkCls} flex items-center gap-1`} data-testid="nav-why-partner">
                Why Partner <ChevronDown className="w-4 h-4" />
              </Link>
              {openMenu === "why" && (
                <div className="absolute top-full left-0 pt-3">
                  <div className="w-64 bg-white rounded-2xl ct-shadow-hover border border-[#EDEFF7] p-2">
                    {whyAnchors.map((a) => (
                      <Link
                        key={a.to}
                        to={a.to}
                        className="block px-4 py-3 rounded-xl text-sm text-[#1E1E24] hover:bg-[#EDEFF7] hover:text-[#0262B4] transition-colors"
                        data-testid={`nav-why-${a.label.replace(/\s+/g, "-").toLowerCase()}`}
                      >
                        {a.label}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div
              className="relative"
              onMouseEnter={() => setOpenMenu("who")}
              onMouseLeave={() => setOpenMenu(null)}
            >
              <Link to="/who-we-serve" className={`${linkCls} flex items-center gap-1`} data-testid="nav-who-we-serve">
                Who We Serve <ChevronDown className="w-4 h-4" />
              </Link>
              {openMenu === "who" && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 pt-3">
                  <div className="w-72 bg-white rounded-2xl ct-shadow-hover border border-[#EDEFF7] p-2">
                    {SEGMENT_LIST.map((s) => (
                      <Link
                        key={s.slug}
                        to={`/who-we-serve/${s.slug}`}
                        className="block px-4 py-3 rounded-xl text-sm text-[#1E1E24] hover:bg-[#EDEFF7] hover:text-[#0262B4] transition-colors"
                        data-testid={`nav-segment-${s.slug}`}
                      >
                        {s.nav}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link to="/how-it-works" className={linkCls} data-testid="nav-how-it-works">
              How It Works
            </Link>
            <Link to="/company" className={linkCls} data-testid="nav-company">
              Company
            </Link>
          </nav>

          <div className="hidden lg:block">
            <CTAButton to="/become-a-partner" variant="primary" testId="nav-cta" className="px-6 py-2.5 text-sm">
              Become a Partner
            </CTAButton>
          </div>

          <button
            className="lg:hidden p-2 text-[#1E1E24]"
            onClick={() => setMobileOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-[#EDEFF7] px-6 py-6 space-y-1" data-testid="nav-mobile-menu">
          <Link to="/why-partner" className="block py-3 font-medium text-[#1E1E24]" data-testid="nav-m-why">Why Partner</Link>
          <Link to="/who-we-serve" className="block py-3 font-medium text-[#1E1E24]" data-testid="nav-m-who">Who We Serve</Link>
          {SEGMENT_LIST.map((s) => (
            <Link key={s.slug} to={`/who-we-serve/${s.slug}`} className="block py-2 pl-4 text-sm text-[#40424D]">
              {s.nav}
            </Link>
          ))}
          <Link to="/how-it-works" className="block py-3 font-medium text-[#1E1E24]" data-testid="nav-m-how">How It Works</Link>
          <Link to="/company" className="block py-3 font-medium text-[#1E1E24]" data-testid="nav-m-company">Company</Link>
          <CTAButton to="/become-a-partner" variant="primary" testId="nav-m-cta" className="w-full mt-4">
            Become a Partner
          </CTAButton>
        </div>
      )}
    </header>
  );
}
